/**
 * mapLabelOffsets.js
 *
 * Pure helper functions for computing pixel-space label collision offsets on
 * Google Maps. These are shared across DashboardMap, MapForIndependentURL,
 * RouteTracking, mapPage, and MapComponent so the logic lives in one place.
 *
 * Nothing here depends on React – these are plain JS functions that you call
 * inside useMemo (or anywhere else) and pass the Google Maps projection/zoom.
 */

// ─── Vehicle label offsets ────────────────────────────────────────────────────
/**
 * Compute per-vehicle label offsets so overlapping vehicle labels stack
 * vertically instead of sitting on top of each other.
 *
 * @param {google.maps.MapCanvasProjection} projection  – map.getProjection()
 * @param {number} zoom                                 – map.getZoom()
 * @param {Array<{deviceId: string, lat: number, lng: number}>} vehicleEntries
 *   Each element must have { deviceId, lat, lng } with numeric lat/lng values.
 * @param {{LABEL_W?: number, LABEL_H?: number, STACK_GAP?: number, BASE_OFFSET?: number}} [opts]
 *   Optional override for label dimensions / spacing.
 * @returns {Object.<string, {top: string, topPx: number, left: string, transform: string}>}
 */
/**
 * Estimate rendered pixel width of a vehicle label.
 * Font: 8px/600, padding: 6px each side = 12px total horizontal padding.
 * Arrow emoji adds ~10px when present.
 *
 * @param {string} [name]
 * @param {boolean} [hasArrow]
 * @returns {number}
 */
function estimateVehicleLabelWidth(name, hasArrow) {
  if (!name) return 60;
  const arrowPx = hasArrow ? 10 : 0;
  return Math.min(180, Math.max(40, Math.round(name.length * 5 + 24 + arrowPx)));
}

export function computeVehicleLabelOffsets(projection, zoom, vehicleEntries, opts = {}) {
  const {
    LABEL_H = 24,   // lineHeight 12px + padding 6px top + 6px bottom
    STACK_GAP = 6,
    // BASE_OFFSET = distance from pin anchor DOWNWARD to the TOP edge of the label.
    BASE_OFFSET = 10,
    // ANCHOR_SHIFT = the "top: Npx" baked into the OverlayView container div.
    // DashboardMap & mapPage use 13px; MapForIndependentURL uses 20px.
    ANCHOR_SHIFT = 13,
  } = opts;

  const scale = Math.pow(2, zoom);

  const pixelPositions = vehicleEntries.map(({ deviceId, lat, lng, name, hasArrow }) => {
    if (isNaN(lat) || isNaN(lng)) return { deviceId, x: null, y: null, labelW: estimateVehicleLabelWidth(name, hasArrow) };
    try {
      const worldPoint = projection.fromLatLngToPoint(
        new window.google.maps.LatLng(lat, lng),
      );
      return {
        deviceId,
        x: worldPoint.x * scale,
        y: worldPoint.y * scale,
        labelW: estimateVehicleLabelWidth(name, hasArrow),
      };
    } catch {
      return { deviceId, x: null, y: null, labelW: estimateVehicleLabelWidth(name, hasArrow) };
    }
  });

  const placed = [];
  const offsets = {};

  pixelPositions.forEach((pos) => {
    if (pos.x === null) {
      const topPx = BASE_OFFSET;
      offsets[pos.deviceId] = {
        topPx,
        top: `${topPx}px`,
        left: "50%",
        transform: "translateX(-50%)",
      };
      placed.push({ x: null, y: null, topPx, labelW: pos.labelW });
      return;
    }

    // ── Actual screen-space label overlap detection ───────────────────────────
    // Vehicle labels are drawn BELOW the pin anchor (positive top).
    // Screen Y of label top centre for a given slot:
    //   labelTopY = pin.y + 13 + BASE_OFFSET + slot * (LABEL_H + STACK_GAP)
    // (pin.y increases downward; adding moves the label downward on screen)
    // Label centre Y = labelTopY + LABEL_H / 2
    //
    // Two labels collide when:
    //   |Δx_centre| < (w1 + w2) / 2
    //   |Δy_centre| < LABEL_H

    const ANCHOR_SHIFT = opts.ANCHOR_SHIFT ?? 13; // use the resolved value from opts

    let slot = 0;
    while (true) {
      const candCentreY = pos.y + ANCHOR_SHIFT + BASE_OFFSET + slot * (LABEL_H + STACK_GAP) + LABEL_H / 2;
      const candCentreX = pos.x;

      const collides = placed.some((p) => {
        if (p.x === null) return false;
        const hThreshold = (pos.labelW + p.labelW) / 2;
        if (Math.abs(candCentreX - p.x) >= hThreshold) return false;
        const pCentreY = p.y + ANCHOR_SHIFT + p.topPx + LABEL_H / 2;
        return Math.abs(candCentreY - pCentreY) < LABEL_H;
      });

      if (!collides) break;
      slot++;
    }

    const topPx = BASE_OFFSET + slot * (LABEL_H + STACK_GAP);
    offsets[pos.deviceId] = {
      topPx,
      top: `${topPx}px`,
      left: "50%",
      transform: "translateX(-50%)",
    };
    placed.push({ x: pos.x, y: pos.y, topPx, labelW: pos.labelW });
  });

  return offsets;
}

// ─── Stop label offsets ───────────────────────────────────────────────────────

/**
 * Estimate the rendered pixel width of a stop label from its name string.
 * Uses ~7.5 px per character plus 16 px for horizontal padding.
 * Clamped between 50 px (very short names) and 200 px (very long names).
 *
 * @param {string} [name]
 * @returns {number}
 */
function estimateLabelWidth(name) {
  if (!name) return 100;
  // 12px font-size, font-weight 500 ≈ 6.5px per char average
  // + 16px horizontal padding (8px each side)
  return Math.min(200, Math.max(50, Math.round(name.length * 6.5 + 16)));
}

/**
 * Compute per-stop label offsets so stop labels that are close together on
 * screen are stacked vertically rather than overlapping.
 *
 * @param {google.maps.MapCanvasProjection} projection  – map.getProjection()
 * @param {number} zoom                                 – map.getZoom()
 * @param {Array<{id: string|number, lat: number, lng: number, name?: string}>} stopEntries
 *   Each element must have { id, lat, lng } with numeric lat/lng values.
 *   Optionally include { name } for accurate per-label width estimation —
 *   without it a default of 100 px is used.
 * @param {{LABEL_H?: number, STACK_GAP?: number, BASE_OFFSET?: number}} [opts]
 *   Optional override for label dimensions / spacing.
 *   NOTE: LABEL_W is intentionally removed from opts — width is now estimated
 *   per-label from the stop name to avoid false collision detection for short
 *   names like "LSG" or "WFS".
 * @returns {Object.<string|number, {topPx: number}>}
 */
export function computeStopLabelOffsets(projection, zoom, stopEntries, opts = {}) {
  const {
    LABEL_H = 26,
    STACK_GAP = 6,
    // BASE_OFFSET = distance from pin-tip to the BOTTOM edge of the label.
    // Icon is 40px tall, so the icon top is at 40px from the tip.
    // A 4px gap between icon top and label bottom gives BASE_OFFSET = 44.
    BASE_OFFSET = 44,
  } = opts;

  const scale = Math.pow(2, zoom);

  const pixelPositions = stopEntries.map(({ id, lat, lng, name }) => {
    try {
      const worldPoint = projection.fromLatLngToPoint(
        new window.google.maps.LatLng(lat, lng),
      );
      return {
        id,
        x: worldPoint.x * scale,
        y: worldPoint.y * scale,
        // Store the estimated label width alongside the pixel position so we
        // can use per-pair thresholds during collision detection.
        labelW: estimateLabelWidth(name),
      };
    } catch {
      return { id, x: null, y: null, labelW: estimateLabelWidth(name) };
    }
  });

  const placed = [];
  const offsets = {};

  pixelPositions.forEach((pos) => {
    if (pos.x === null) {
      offsets[pos.id] = { topPx: BASE_OFFSET };
      placed.push({ x: null, y: null, topPx: BASE_OFFSET, labelW: pos.labelW, slot: 0 });
      return;
    }

    // ── Actual screen-space label overlap detection ───────────────────────────
    //
    // For each candidate slot, compute where THIS label's centre would appear
    // on screen and check if it overlaps any already-placed label's actual
    // screen rectangle. This avoids cascading: stop 3 only bumps if its label
    // at that slot would truly collide with an existing label — not because
    // stop 2 happened to bump due to stop 1.
    //
    // Screen Y of a label's vertical centre for a given stop and slot:
    //   labelCentreY = pin.y - (BASE_OFFSET + slot * (LABEL_H + STACK_GAP)) - LABEL_H/2
    // (pin.y increases downward; subtracting moves the label upward on screen)
    //
    // Two labels collide when:
    //   |Δx_centre| < (w1 + w2) / 2   (horizontal overlap)
    //   |Δy_centre| < LABEL_H          (vertical overlap)

    let slot = 0;
    while (true) {
      // Screen-space centre of candidate label
      const candY = pos.y - (BASE_OFFSET + slot * (LABEL_H + STACK_GAP)) - LABEL_H / 2;
      const candX = pos.x; // label is horizontally centred on the pin

      const collides = placed.some((p) => {
        if (p.x === null) return false;
        // Horizontal: half-widths must not overlap
        const hThreshold = (pos.labelW + p.labelW) / 2;
        if (Math.abs(candX - p.x) >= hThreshold) return false;
        // Vertical: compare against p's actual assigned label centre
        const pLabelCentreY = p.y - p.topPx - LABEL_H / 2;
        return Math.abs(candY - pLabelCentreY) < LABEL_H;
      });

      if (!collides) break;
      slot++;
    }

    const assignedTop = BASE_OFFSET + slot * (LABEL_H + STACK_GAP);
    offsets[pos.id] = { topPx: assignedTop };
    placed.push({ x: pos.x, y: pos.y, topPx: assignedTop, labelW: pos.labelW, slot });
  });

  return offsets;
}